import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { breakpoints } from '@rover/kibble/themes/themeConstants';

import type { ImageSize } from './hooks/useImageSize.common';
import {
  LIGHTBOX_ARROW_GUTTER,
  LIGHTBOX_RESERVED_HEIGHT,
  MAX_LIGHTBOX_WEB_IMAGE_WIDTH,
  MIN_LIGHTBOX_IMAGE_HEIGHT,
} from './constants';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import { isGalleryVideoItem } from './ImageGallery.common';

const TABLET_MIN_WIDTH = parseInt(breakpoints[0], 10);

export const getShimmerCount = (
  pagination: PaginationConfig | undefined,
  loadedCount: number
): number => {
  if (!pagination?.pageSize) return 0;
  const remaining = pagination.totalImageCount - loadedCount;
  return Math.min(pagination.pageSize, Math.max(remaining, 0));
};

export const shouldLoadMore = (pagination: PaginationConfig | undefined): boolean =>
  Boolean(pagination?.hasMore && !pagination?.isLoadingMore);

export const showsLoadMoreButton = (pagination: PaginationConfig | undefined): boolean =>
  Boolean(pagination?.hasMore && pagination.loadMoreVariant === 'button');

type LightboxBoundsOptions = {
  /** The overlay always scrolls, so a classic scrollbar track narrows its content. */
  scrollbarWidth?: number;
  belowStageHeight?: number;
};

/**
 * The box a lightbox photo is fitted into. On a phone the photo runs edge to
 * edge under the arrows, as the overlay drops its side padding there.
 */
export const getLightboxImageBounds = (
  viewport: ImageSize,
  { scrollbarWidth = 0, belowStageHeight = 0 }: LightboxBoundsOptions = {}
): ImageSize => {
  // The arrow gutters are wider than a scrollbar track, so only a phone-width overlay loses room to one.
  const reservedWidth =
    viewport.width < TABLET_MIN_WIDTH ? scrollbarWidth : 2 * LIGHTBOX_ARROW_GUTTER;
  const reservedHeight = LIGHTBOX_RESERVED_HEIGHT + belowStageHeight;

  return {
    width: Math.max(0, Math.min(viewport.width - reservedWidth, MAX_LIGHTBOX_WEB_IMAGE_WIDTH)),
    height: Math.max(viewport.height - reservedHeight, MIN_LIGHTBOX_IMAGE_HEIGHT),
  };
};

/** The largest 4:3 box within `bounds`, the shape of most photos, held while one loads. */
export const getLightboxPlaceholderSize = (bounds: ImageSize): ImageSize => {
  const width = Math.min(bounds.width, (bounds.height * 4) / 3);

  return { width: Math.round(width), height: Math.round((width * 3) / 4) };
};

/**
 * A grid tile's names. `captionLabel` says the tile opens the lightbox, even when the photo has
 * a caption of its own; a tile that presses nowhere keeps the plain `photoName`.
 */
export const getTileLabels = (
  i18n: I18n,
  item: GalleryItem,
  index: number,
  opensLightbox: boolean
): { photoName: string; captionLabel: string } => {
  const photoNumber = index + 1;
  const isVideo = isGalleryVideoItem(item);
  // The number only stands in for a photo with no description.
  const photoName =
    (isVideo ? item.video.description : item.altText || item.caption) ||
    i18n._(msg`photo ${photoNumber}`);

  let captionLabel: string;
  if (isVideo) {
    captionLabel = item.video.description;
  } else if (!opensLightbox) {
    captionLabel = photoName;
  } else if (item.caption) {
    const ownCaption = item.caption;
    captionLabel = i18n._(msg`View photo ${photoNumber}: ${ownCaption}`);
  } else {
    captionLabel = i18n._(msg`View photo ${photoNumber}`);
  }

  return { photoName, captionLabel };
};
